// src/components/users/UserForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { createUser, updateUser, fetchUser } from '../../store/actions/userActions';
import { fetchRoles } from '../../store/actions/roleActions';
import {
  fetchDepartments,
  fetchTeams,
  fetchManagers,
  fetchEmployeeTypes,
  fetchEmploymentStatuses,
  fetchAccessLevels,
} from '../../store/actions/referenceActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedUser, loading, error, success } = useSelector((state) => state.users);
  const { roles } = useSelector((state) => state.roles);
  const {
    departments,
    teams,
    managers,
    employeeTypes,
    employmentStatuses,
    accessLevels,
    loading: refLoading,
  } = useSelector((state) => state.reference);

  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    // Basic Identifiers
    email: '',
    workEmail: '',
    employeeId: '',
    username: '',

    // Personal Information
    title: '',
    firstName: '',
    middleName: '',
    lastName: '',
    displayName: '',
    dateOfBirth: '',
    gender: '',
    pronoun: '',

    // Professional Information
    jobTitle: '',
    department: '',
    team: '',
    division: '',
    costCenter: '',
    employeeType: 'FULL_TIME',
    employmentStatus: 'ACTIVE',

    // Contact Information
    phoneNumber: '',
    workPhone: '',
    mobilePhone: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',

    // Work Location
    officeLocation: '',
    floor: '',
    deskNumber: '',
    timezone: 'UTC',
    language: 'en',

    // Employment Details
    hireDate: '',
    startDate: '',
    probationEndDate: '',
    contractEndDate: '',

    // Manager
    managerId: '',

    // Compensation
    salary: '',
    currency: 'USD',
    payGrade: '',
    bonusEligible: false,
    stockOptions: 0,

    // Skills & Qualifications
    skills: [],
    certifications: [],
    bio: '',
    expertise: '',

    // System
    isActive: true,
    accessLevel: 'BASIC',
    requirePasswordReset: true,

    // Roles
    roles: [],
  });

  const [skillInput, setSkillInput] = useState('');
  const [certificationInput, setCertificationInput] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Styles object
  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      marginBottom: '2rem',
      backgroundColor: '#f9fafb',
    },
    title: {
      fontSize: '1.875rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    subtitle: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
    
    // Tabs
    tabsContainer: {
      display: 'flex',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
      marginBottom: '2rem',
      overflowX: 'auto',
      whiteSpace: 'nowrap',
    },
    tab: {
      padding: '0.75rem 1.5rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
      background: 'none',
      border: 'none',
      borderBottom: '2px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    tabActive: {
      color: '#2563eb',
      borderBottomColor: '#2563eb',
    },
    tabHover: {
      color: '#374151',
    },
    
    // Form
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    formSection: {
      padding: '1.5rem',
      borderBottom: '1px solid #e5e7eb',
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    sectionDescription: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '1rem',
    },
    
    // Grid layouts
    grid2: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1rem',
    },
    grid3: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '1rem',
    },
    grid4: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '1rem',
    },
    
    // Form fields
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
    },
    required: {
      color: '#ef4444',
      marginLeft: '0.25rem',
    },
    input: {
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
      width: '100%',
    },
    textarea: {
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
      width: '100%',
      minHeight: '6rem',
      resize: 'vertical',
    },
    select: {
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
      width: '100%',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1.5em 1.5em',
      paddingRight: '2.5rem',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      accentColor: '#2563eb',
      cursor: 'pointer',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
    },
    
    // Field states
    inputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    inputError: {
      borderColor: '#ef4444',
    },
    inputErrorFocus: {
      borderColor: '#ef4444',
      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
    },
    errorMessage: {
      fontSize: '0.75rem',
      color: '#ef4444',
      marginTop: '0.25rem',
    },
    helperText: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
    
    // Array fields (skills, certifications)
    arrayFieldContainer: {
      marginBottom: '1rem',
    },
    arrayInputGroup: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '0.5rem',
    },
    arrayInput: {
      flex: 1,
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'all 0.2s',
    },
    addButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    addButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    chipsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
    },
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.25rem 0.75rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      fontSize: '0.875rem',
      borderRadius: '9999px',
    },
    chipRemove: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1rem',
      height: '1rem',
      borderRadius: '9999px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '1rem',
      lineHeight: 1,
      color: '#6b7280',
      transition: 'color 0.2s',
      padding: 0,
    },
    chipRemoveHover: {
      color: '#ef4444',
    },
    
    // Role selection
    rolesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '0.75rem',
      maxHeight: '400px',
      overflowY: 'auto',
      padding: '0.5rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
    },
    roleCheckbox: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '0.75rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    roleCheckboxHover: {
      backgroundColor: '#f9fafb',
    },
    roleCheckboxSelected: {
      backgroundColor: '#eff6ff',
      borderColor: '#2563eb',
    },
    roleCheckboxInput: {
      marginTop: '0.125rem',
      width: '1rem',
      height: '1rem',
      accentColor: '#2563eb',
      cursor: 'pointer',
    },
    roleContent: {
      marginLeft: '0.75rem',
      flex: 1,
    },
    roleName: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    },
    roleDescription: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginTop: '0.125rem',
    },
    
    // Form actions
    formActions: {
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    submitButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    
    // Progress indicator
    progressContainer: {
      display: 'flex',
      backgroundColor: '#f9fafb',
      justifyContent: 'space-between',
      marginBottom: '2rem',
      padding: '0 1rem',
    },
    progressStep: {
      flex: 1,
      textAlign: 'center',
      position: 'relative',
    },
    progressStepLine: {
      position: 'absolute',
      top: '1rem',
      left: '50%',
      right: '-50%',
      height: '2px',
      backgroundColor: '#e5e7eb',
      zIndex: 1,
    },
    progressStepCircle: {
      width: '2rem',
      height: '2rem',
      borderRadius: '9999px',
      backgroundColor: '#ffffff',
      border: '2px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 0.5rem',
      position: 'relative',
      zIndex: 2,
      transition: 'all 0.2s',
    },
    progressStepCircleActive: {
      backgroundColor: '#2563eb',
      borderColor: '#2563eb',
      color: '#ffffff',
    },
    progressStepCircleCompleted: {
      backgroundColor: '#10b981',
      borderColor: '#10b981',
      color: '#ffffff',
    },
    progressStepLabel: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    progressStepLabelActive: {
      color: '#2563eb',
      fontWeight: 500,
    },
    
    // Loading overlay
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'personal', label: 'Personal' },
    { id: 'professional', label: 'Professional' },
    { id: 'contact', label: 'Contact' },
    { id: 'employment', label: 'Employment' },
    { id: 'skills', label: 'Skills' },
    { id: 'roles', label: 'Roles' },
    { id: 'system', label: 'System' },
  ];

  useEffect(() => {
    // Fetch reference data
    dispatch(fetchDepartments());
    dispatch(fetchTeams());
    dispatch(fetchManagers());
    dispatch(fetchEmployeeTypes());
    dispatch(fetchEmploymentStatuses());
    dispatch(fetchAccessLevels());
    dispatch(fetchRoles({ limit: 100 }));

    if (id) {
      dispatch(fetchUser(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (selectedUser && id) {
      setFormData({
        email: selectedUser.email || '',
        workEmail: selectedUser.workEmail || '',
        employeeId: selectedUser.employeeId || '',
        username: selectedUser.username || '',
        title: selectedUser.title || '',
        firstName: selectedUser.firstName || '',
        middleName: selectedUser.middleName || '',
        lastName: selectedUser.lastName || '',
        displayName: selectedUser.displayName || '',
        dateOfBirth: selectedUser.dateOfBirth ? selectedUser.dateOfBirth.split('T')[0] : '',
        gender: selectedUser.gender || '',
        pronoun: selectedUser.pronoun || '',
        jobTitle: selectedUser.jobTitle || '',
        department: selectedUser.department || '',
        team: selectedUser.team || '',
        division: selectedUser.division || '',
        costCenter: selectedUser.costCenter || '',
        employeeType: selectedUser.employeeType || 'FULL_TIME',
        employmentStatus: selectedUser.employmentStatus || 'ACTIVE',
        phoneNumber: selectedUser.phoneNumber || '',
        workPhone: selectedUser.workPhone || '',
        mobilePhone: selectedUser.mobilePhone || '',
        emergencyContactName: selectedUser.emergencyContactName || '',
        emergencyContactPhone: selectedUser.emergencyContactPhone || '',
        emergencyContactRelationship: selectedUser.emergencyContactRelationship || '',
        officeLocation: selectedUser.officeLocation || '',
        floor: selectedUser.floor || '',
        deskNumber: selectedUser.deskNumber || '',
        timezone: selectedUser.timezone || 'UTC',
        language: selectedUser.language || 'en',
        hireDate: selectedUser.hireDate ? selectedUser.hireDate.split('T')[0] : '',
        startDate: selectedUser.startDate ? selectedUser.startDate.split('T')[0] : '',
        probationEndDate: selectedUser.probationEndDate ? selectedUser.probationEndDate.split('T')[0] : '',
        contractEndDate: selectedUser.contractEndDate ? selectedUser.contractEndDate.split('T')[0] : '',
        managerId: selectedUser.managerId || '',
        salary: selectedUser.salary || '',
        currency: selectedUser.currency || 'USD',
        payGrade: selectedUser.payGrade || '',
        bonusEligible: selectedUser.bonusEligible || false,
        stockOptions: selectedUser.stockOptions || 0,
        skills: selectedUser.skills || [],
        certifications: selectedUser.certifications || [],
        bio: selectedUser.bio || '',
        expertise: selectedUser.expertise || '',
        isActive: selectedUser.isActive !== undefined ? selectedUser.isActive : true,
        accessLevel: selectedUser.accessLevel || 'BASIC',
        requirePasswordReset: selectedUser.requirePasswordReset !== undefined ? selectedUser.requirePasswordReset : true,
      });

      setSelectedRoles(selectedUser.roles?.map(r => r.id) || []);
    }
  }, [selectedUser, id]);

  const validateField = useCallback((name, value) => {
    const errors = {};

    switch (name) {
      case 'email':
        if (!value) {
          errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          errors.email = 'Email is invalid';
        }
        break;
      case 'firstName':
        if (!value) {
          errors.firstName = 'First name is required';
        }
        break;
      case 'lastName':
        if (!value) {
          errors.lastName = 'Last name is required';
        }
        break;
      case 'phoneNumber':
        if (value && !/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/.test(value)) {
          errors.phoneNumber = 'Phone number is invalid';
        }
        break;
      case 'salary':
        if (value && isNaN(parseFloat(value))) {
          errors.salary = 'Salary must be a number';
        }
        break;
      case 'stockOptions':
        if (value && isNaN(parseInt(value))) {
          errors.stockOptions = 'Stock options must be a number';
        }
        break;
      default:
        break;
    }

    return errors;
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Validate on change if field has been touched
    if (touched[name]) {
      const fieldErrors = validateField(name, newValue);
      setFormErrors((prev) => ({
        ...prev,
        ...fieldErrors,
      }));
    }
  }, [touched, validateField]);

  const handleBlur = useCallback((e) => {
    const { name, value } = e.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const fieldErrors = validateField(name, value);
    setFormErrors((prev) => ({
      ...prev,
      ...fieldErrors,
    }));
  }, [validateField]);

  const handleArrayAdd = useCallback((field, value, setInput) => {
    if (value && !formData[field].includes(value)) {
      setFormData((prev) => ({
        ...prev,
        [field]: [...prev[field], value],
      }));
      setInput('');
    }
  }, [formData]);

  const handleArrayRemove = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((item) => item !== value),
    }));
  }, []);

  const handleRoleToggle = useCallback((roleId) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId]
    );
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Validate all fields
    const allErrors = {};
    Object.keys(formData).forEach((key) => {
      const fieldErrors = validateField(key, formData[key]);
      Object.assign(allErrors, fieldErrors);
    });

    if (Object.keys(allErrors).length > 0) {
      setFormErrors(allErrors);
      // Set all fields as touched
      const allTouched = {};
      Object.keys(formData).forEach((key) => {
        allTouched[key] = true;
      });
      setTouched(allTouched);
      return;
    }

    // Prepare data for submission
    const submitData = {
      ...formData,
      roles: selectedRoles,
      salary: formData.salary ? parseFloat(formData.salary) : undefined,
      stockOptions: formData.stockOptions ? parseInt(formData.stockOptions) : undefined,
      skills: formData.skills,
      certifications: formData.certifications,
    };

    // Remove empty strings and undefined values
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === '' || submitData[key] === undefined) {
        delete submitData[key];
      }
    });

    try {
      if (id) {
        await dispatch(updateUser(id, submitData));
      } else {
        const result = await dispatch(createUser(submitData));
        if (result.temporaryPassword) {
          // Handle temporary password display
          alert(`Temporary Password: ${result.temporaryPassword}`);
        }
      }
      navigate('/admin/users');
    } catch (error) {
      console.error('Form submission error:', error);
    }
  }, [dispatch, id, formData, selectedRoles, validateField, navigate]);

  const renderField = (name, label, type = 'text', options = {}) => {
    const {
      required = false,
      placeholder = '',
      options: selectOptions = [],
      grid = 'full',
      helperText = '',
    } = options;

    const value = formData[name] || '';
    const error = formErrors[name];
    const isTouched = touched[name];

    const fieldStyle = {
      ...styles.input,
      ...(isTouched && error ? styles.inputError : {}),
    };

    return (
      <div style={styles.fieldGroup} className={`grid-${grid}`}>
        <label style={styles.label}>
          {label}
          {required && <span style={styles.required}>*</span>}
        </label>
        
        {type === 'textarea' ? (
          <textarea
            name={name}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            style={fieldStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = isTouched && error 
                ? styles.inputErrorFocus.borderColor 
                : styles.inputFocus.borderColor;
              e.currentTarget.style.boxShadow = isTouched && error 
                ? styles.inputErrorFocus.boxShadow 
                : styles.inputFocus.boxShadow;
            }}
            onBlurCapture={(e) => {
              e.currentTarget.style.borderColor = isTouched && error 
                ? styles.inputError.borderColor 
                : '#d1d5db';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        ) : type === 'select' ? (
          <select
            name={name}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            style={fieldStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = isTouched && error 
                ? styles.inputErrorFocus.borderColor 
                : styles.inputFocus.borderColor;
              e.currentTarget.style.boxShadow = isTouched && error 
                ? styles.inputErrorFocus.boxShadow 
                : styles.inputFocus.boxShadow;
            }}
            onBlurCapture={(e) => {
              e.currentTarget.style.borderColor = isTouched && error 
                ? styles.inputError.borderColor 
                : '#d1d5db';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <option value="">Select {label}</option>
            {selectOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : type === 'checkbox' ? (
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              name={name}
              checked={value}
              onChange={handleChange}
              style={styles.checkbox}
            />
            {placeholder}
          </label>
        ) : (
          <input
            type={type}
            name={name}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            style={fieldStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = isTouched && error 
                ? styles.inputErrorFocus.borderColor 
                : styles.inputFocus.borderColor;
              e.currentTarget.style.boxShadow = isTouched && error 
                ? styles.inputErrorFocus.boxShadow 
                : styles.inputFocus.boxShadow;
            }}
            onBlurCapture={(e) => {
              e.currentTarget.style.borderColor = isTouched && error 
                ? styles.inputError.borderColor 
                : '#d1d5db';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        )}
        
        {error && isTouched && (
          <div style={styles.errorMessage}>{error}</div>
        )}
        
        {helperText && !error && (
          <div style={styles.helperText}>{helperText}</div>
        )}
      </div>
    );
  };

  if (loading || refLoading) {
    return (
      <div style={styles.loadingOverlay}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {id ? 'Editer l\'utilisateur' : 'Créer un nouvel utilisateur'}
        </h1>
        <p style={styles.subtitle}>
          {id ? 'Mettre à jour les informations et les paramètres de l\'utilisateur' : 'Ajouter un nouvel utilisateur au système'}
        </p>
      </div>

      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      {/* Progress Steps */}
      <div style={styles.progressContainer}>
        {tabs.map((tab, index) => (
          <div key={tab.id} style={styles.progressStep}>
            {index < tabs.length - 1 && (
              <div style={styles.progressStepLine} />
            )}
            <div style={{
              ...styles.progressStepCircle,
              ...(activeTab === tab.id ? styles.progressStepCircleActive : {}),
              ...(index < tabs.findIndex(t => t.id === activeTab) ? styles.progressStepCircleCompleted : {}),
            }}>
              {index < tabs.findIndex(t => t.id === activeTab) ? '✓' : index + 1}
            </div>
            <div style={{
              ...styles.progressStepLabel,
              ...(activeTab === tab.id ? styles.progressStepLabelActive : {}),
            }}>
              {tab.label}
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={styles.tabsContainer}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = styles.tabHover.color;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.color = styles.tab.color;
              }
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Basic Information */}
        {activeTab === 'basic' && (
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Informations de base</h2>
            <div style={styles.grid2}>
              {renderField('email', 'Email', 'email', { required: true })}
              {renderField('workEmail', 'Email professionnel', 'email')}
              {renderField('employeeId', 'Employee ID')}
              {renderField('username', 'Nom d\'utilisateur')}
            </div>
          </div>
        )}

        {/* Personal Information */}
        {activeTab === 'personal' && (
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Informations personnelles</h2>
            <div style={styles.grid3}>
              {renderField('title', 'Titre', 'select', {
                options: [
                  { value: 'Mr', label: 'Mr' },
                  { value: 'Mrs', label: 'Mrs' },
                  { value: 'Ms', label: 'Ms' },
                  { value: 'Dr', label: 'Dr' },
                  { value: 'Prof', label: 'Prof' },
                ],
              })}
              {renderField('firstName', 'Prénom', 'text', { required: true })}
              {renderField('middleName', 'Deuxième prénom')}
              {renderField('lastName', 'Nom de famille', 'text', { required: true })}
              {renderField('displayName', 'Nom d\'affichage')}
              {renderField('dateOfBirth', 'Date de naissance', 'date')}
              {renderField('gender', 'Genre', 'select', {
                options: [
                  { value: 'Male', label: 'Male' },
                  { value: 'Female', label: 'Female' },
                  { value: 'Non-binary', label: 'Non-binary' },
                  { value: 'Prefer not to say', label: 'Prefer not to say' },
                ],
              })}
              {renderField('pronoun', 'Pronoun', 'select', {
                options: [
                  { value: 'He/Him', label: 'He/Him' },
                  { value: 'She/Her', label: 'She/Her' },
                  { value: 'They/Them', label: 'They/Them' },
                ],
              })}
            </div>
          </div>
        )}

        {/* Professional Information */}
        {activeTab === 'professional' && (
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Informations professionnelles</h2>
            <div style={styles.grid2}>
              {renderField('jobTitle', 'Poste')}
              {renderField('department', 'Département', 'select', {
                options: departments.map(d => ({ value: d, label: d })),
              })}
              {renderField('team', 'Équipe', 'select', {
                options: teams.map(t => ({ value: t, label: t })),
              })}
              {renderField('division', 'Division')}
              {renderField('costCenter', 'Centre de coûts')}
              {renderField('employeeType', 'Type d\'employé', 'select', {
                options: employeeTypes.map(t => ({ value: t.value, label: t.label })),
              })}
              {renderField('employmentStatus', 'Statut d\'emploi', 'select', {
                options: employmentStatuses.map(s => ({ value: s.value, label: s.label })),
              })}
            </div>
          </div>
        )}

        {/* Contact Information */}
        {activeTab === 'contact' && (
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Informations de contact</h2>
            <div style={styles.grid2}>
              {renderField('phoneNumber', 'Numéro de téléphone', 'tel')}
              {renderField('workPhone', 'Téléphone professionnel', 'tel')}
              {renderField('mobilePhone', 'Téléphone mobile', 'tel')}
              {renderField('officeLocation', 'Emplacement de bureau')}
              {renderField('floor', 'Étage')}
              {renderField('deskNumber', 'Numéro de bureau')}
              {renderField('timezone', 'Fuseau horaire', 'select', {
                options: [
                  { value: 'UTC', label: 'UTC' },
                  { value: 'America/New_York', label: 'Eastern Time' },
                  { value: 'America/Chicago', label: 'Central Time' },
                  { value: 'America/Denver', label: 'Mountain Time' },
                  { value: 'America/Los_Angeles', label: 'Pacific Time' },
                  { value: 'Europe/London', label: 'London' },
                  { value: 'Europe/Paris', label: 'Paris' },
                  { value: 'Asia/Tokyo', label: 'Tokyo' },
                ],
              })}
              {renderField('language', 'Langue', 'select', {
                options: [
                  { value: 'en', label: 'English' },
                  { value: 'fr', label: 'French' },
                  { value: 'es', label: 'Spanish' },
                  { value: 'de', label: 'German' },
                  { value: 'zh', label: 'Chinese' },
                ],
              })}
            </div>

            <h3 style={{ ...styles.sectionTitle, marginTop: '1.5rem' }}>Contact d\'urgence</h3>
            <div style={styles.grid2}>
              {renderField('emergencyContactName', 'Nom du contact')}
              {renderField('emergencyContactPhone', 'Téléphone du contact', 'tel')}
              {renderField('emergencyContactRelationship', 'Relation avec le contact')}
            </div>
          </div>
        )}

        {/* Employment Details */}
        {activeTab === 'employment' && (
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Détails d\'emploi</h2>
            <div style={styles.grid2}>
              {renderField('hireDate', 'Date d\'embauche', 'date')}
              {renderField('startDate', 'Date de début', 'date')}
              {renderField('probationEndDate', 'Date de fin de probation', 'date')}
              {renderField('contractEndDate', 'Date de fin de contrat', 'date')}
              {renderField('managerId', 'Manager', 'select', {
                options: managers.map(m => ({ value: m.id, label: m.name })),
              })}
            </div>

            <h3 style={{ ...styles.sectionTitle, marginTop: '1.5rem' }}>Compensation</h3>
            <div style={styles.grid3}>
              {renderField('salary', 'Salaire', 'number')}
              {renderField('currency', 'Devise', 'select', {
                options: [
                  { value: 'USD', label: 'USD' },
                  { value: 'EUR', label: 'EUR' },
                  { value: 'GBP', label: 'GBP' },
                ],
              })}
              {renderField('payGrade', 'Grade de paie')}
              {renderField('bonusEligible', 'Bonus Eligible', 'checkbox', {
                placeholder: 'Eligible for bonus',
              })}
              {renderField('stockOptions', 'Options d\'actions', 'number')}
            </div>
          </div>
        )}

        {/* Skills & Qualifications */}
        {activeTab === 'skills' && (
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Compétences et Qualifications</h2>
            
            {/* Skills */}
            <div style={styles.arrayFieldContainer}>
              <label style={styles.label}>Compétences</label>
              <div style={styles.arrayInputGroup}>
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleArrayAdd('skills', skillInput, setSkillInput);
                    }
                  }}
                  placeholder="Ajouter une compétence"
                  style={styles.arrayInput}
                />
                <button
                  type="button"
                  onClick={() => handleArrayAdd('skills', skillInput, setSkillInput)}
                  style={styles.addButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = styles.addButtonHover.backgroundColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = styles.addButton.backgroundColor;
                  }}
                >
                  Ajouter
                </button>
              </div>
              <div style={styles.chipsContainer}>
                {formData.skills.map((skill) => (
                  <span key={skill} style={styles.chip}>
                    {skill}
                    <button
                      type="button"
                      onClick={() => handleArrayRemove('skills', skill)}
                      style={styles.chipRemove}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.chipRemoveHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.chipRemove.color;
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div style={styles.arrayFieldContainer}>
              <label style={styles.label}>Certifications</label>
              <div style={styles.arrayInputGroup}>
                <input
                  type="text"
                  value={certificationInput}
                  onChange={(e) => setCertificationInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleArrayAdd('certifications', certificationInput, setCertificationInput);
                    }
                  }}
                  placeholder="Ajouter une certification"
                  style={styles.arrayInput}
                />
                <button
                  type="button"
                  onClick={() => handleArrayAdd('certifications', certificationInput, setCertificationInput)}
                  style={styles.addButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = styles.addButtonHover.backgroundColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = styles.addButton.backgroundColor;
                  }}
                >
                  Add
                </button>
              </div>
              <div style={styles.chipsContainer}>
                {formData.certifications.map((cert) => (
                  <span key={cert} style={styles.chip}>
                    {cert}
                    <button
                      type="button"
                      onClick={() => handleArrayRemove('certifications', cert)}
                      style={styles.chipRemove}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.chipRemoveHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.chipRemove.color;
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Bio */}
            <div>
              {renderField('bio', 'Bio', 'textarea')}
            </div>
          </div>
        )}

        {/* Role Assignment */}
        {activeTab === 'roles' && (
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Attribution des rôles</h2>
            <p style={styles.sectionDescription}>
             Sélectionnez un ou plusieurs rôles à attribuer à cet utilisateur. Les rôles déterminent les permissions dont dispose l'utilisateur.
            </p>
            <div style={styles.rolesGrid}>
              {roles.map((role) => {
                const isSelected = selectedRoles.includes(role.id);
                return (
                  <div
                    key={role.id}
                    style={{
                      ...styles.roleCheckbox,
                      ...(isSelected ? styles.roleCheckboxSelected : {}),
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = styles.roleCheckboxHover.backgroundColor;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                    onClick={() => handleRoleToggle(role.id)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      style={styles.roleCheckboxInput}
                    />
                    <div style={styles.roleContent}>
                      <div style={styles.roleName}>{role.name}</div>
                      {role.description && (
                        <div style={styles.roleDescription}>{role.description}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* System Settings */}
        {activeTab === 'system' && (
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Paramètres système</h2>
            <div style={styles.grid2}>
              {renderField('accessLevel', 'Niveau d\'accès', 'select', {
                options: accessLevels.map(l => ({ value: l.value, label: l.label })),
              })}
              {renderField('isActive', 'Actif', 'checkbox', {
                placeholder: 'User account is active',
              })}
              {renderField('requirePasswordReset', 'Réinitialisation du mot de passe', 'checkbox', {
                placeholder: 'Require password reset on next login',
              })}
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div style={styles.formActions}>
          <button
            type="button"
            onClick={() => navigate('/admin/users')}
            style={styles.cancelButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
            }}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitButton,
              ...(loading ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {loading ? 'Enregistrement...' : id ? 'Mettre à jour l\'utilisateur' : 'Créer un utilisateur'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;